// Typed convenience wrappers over callConsoleCustomer for every customer
// RPC the portal uses (ADR-179 D2.2). Mirrors console-api.ts's shape
// exactly (thin pass-throughs, no business logic) but funnels through the
// customer transport (lib/auth/client.ts), never the operator's.
import { callConsoleCustomer, callConsoleCustomerUnauthenticated } from '@/lib/auth/client'
import * as pb from '@/generated/console_pb'

// ---------------- Principal introspection ----------------

// whoAmI (customer branch) returns the caller's own CustomerUser identity —
// {userId, accountId, email, role}. The response's getCustomer() half is
// populated for a customer_session cookie call (never getOperator()) —
// see auth-context.tsx's toCustomerPrincipal for the read side.
export function whoAmI(): Promise<pb.WhoAmIResponse> {
  const r = new pb.WhoAmIRequest()
  return callConsoleCustomer('whoAmI', r)
}

export function changeMyPassword(
  oldPassword: string,
  newPassword: string,
): Promise<pb.ChangeMyPasswordResponse> {
  const r = new pb.ChangeMyPasswordRequest()
  r.setOldPassword(oldPassword)
  r.setNewPassword(newPassword)
  return callConsoleCustomer('changeMyPassword', r)
}

// ---------------- Licenses ----------------

// listMyLicenses takes no fields — the account is forced server-side from
// the resolved customer_sessions row, never a request field (ADR-179 D2.2).
export function listMyLicenses(): Promise<pb.ListMyLicensesResponse> {
  const r = new pb.ListMyLicensesRequest()
  return callConsoleCustomer('listMyLicenses', r)
}

export function getMyLicense(licenseId: string): Promise<pb.GetMyLicenseResponse> {
  const r = new pb.GetMyLicenseRequest()
  r.setLicenseId(licenseId)
  return callConsoleCustomer('getMyLicense', r)
}

// downloadMyLicense returns the signed license bundle (License with its
// signed_token/public_key_fingerprint populated) — read-only, no private
// key material (ADR-179 D2.2: "the public-key/fingerprint bundle...no key
// material a customer shouldn't hold").
export function downloadMyLicense(licenseId: string): Promise<pb.DownloadMyLicenseResponse> {
  const r = new pb.DownloadMyLicenseRequest()
  r.setLicenseId(licenseId)
  return callConsoleCustomer('downloadMyLicense', r)
}

// ---------------- Usage ----------------

export function listMyUsageEvents(req: {
  instanceId?: string
  orgId?: string
  eventType?: string
  pageSize?: number
  pageToken?: string
}): Promise<pb.ListMyUsageEventsResponse> {
  const r = new pb.ListMyUsageEventsRequest()
  if (req.instanceId) r.setInstanceId(req.instanceId)
  if (req.orgId) r.setOrgId(req.orgId)
  if (req.eventType) r.setEventType(req.eventType)
  r.setPageSize(req.pageSize ?? 50)
  if (req.pageToken) r.setPageToken(req.pageToken)
  return callConsoleCustomer('listMyUsageEvents', r)
}

// ---------------- Billing ----------------

// getMyContractStatus reads the account-scoped Console CONTRACT (not
// per-org Stripe billing — see ADR-179 D2.2's billing-scoping caveat: the
// rich per-org Stripe self-service is a separate P3+ federation task).
// license_id is optional — omitted, the server resolves the account's
// primary/only license.
export function getMyContractStatus(licenseId?: string): Promise<pb.GetMyContractStatusResponse> {
  const r = new pb.GetMyContractStatusRequest()
  if (licenseId) r.setLicenseId(licenseId)
  return callConsoleCustomer('getMyContractStatus', r)
}

// listMyOrgBillingPortals is the ADR-179 P3 / D2.2 federation call —
// resolves the session account's orgs server-side and returns a per-org
// Stripe portal link (or an empty portal_url per org, plus
// billing_unavailable=true, when services/billing isn't reachable/
// configured — see the proto doc, never a thrown error for that case).
export function listMyOrgBillingPortals(returnUrl?: string): Promise<pb.ListMyOrgBillingPortalsResponse> {
  const r = new pb.ListMyOrgBillingPortalsRequest()
  if (returnUrl) r.setReturnUrl(returnUrl)
  return callConsoleCustomer('listMyOrgBillingPortals', r)
}

// requestSeatChange / requestCancellation are the ADR-179 P3 notify-operator
// RPCs (section 7-D) — neither mutates anything; both write a durable
// operator-visible ticket and return immediately.
export function requestSeatChange(requestedSeats: number, detail: string): Promise<pb.RequestSeatChangeResponse> {
  const r = new pb.RequestSeatChangeRequest()
  r.setRequestedSeats(requestedSeats)
  r.setDetail(detail)
  return callConsoleCustomer('requestSeatChange', r)
}

export function requestCancellation(detail: string): Promise<pb.RequestCancellationResponse> {
  const r = new pb.RequestCancellationRequest()
  r.setDetail(detail)
  return callConsoleCustomer('requestCancellation', r)
}

// requestSupport is ADR-186's portal "Get help" affordance — a third
// notify-operator write into the SAME operator_requests inbox
// RequestSeatChange/RequestCancellation use, but reachable by ANY account
// role (not owner/admin-gated) since filing a bespoke help request is not
// a governance action. instanceId is optional — this build's UI does not
// offer an instance selector (see ADR-186 D5), so callers normally omit it.
export function requestSupport(subject: string, detail: string, instanceId?: string): Promise<pb.RequestSupportResponse> {
  const r = new pb.RequestSupportRequest()
  r.setSubject(subject)
  r.setDetail(detail)
  if (instanceId) r.setInstanceId(instanceId)
  return callConsoleCustomer('requestSupport', r)
}

// ---------------- Member management (ADR-179 P3 / section 7-E) ----------------

export function listMembers(): Promise<pb.ListMembersResponse> {
  const r = new pb.ListMembersRequest()
  return callConsoleCustomer('listMembers', r)
}

// inviteMember returns the one-time invite token/URL directly in the
// response — this build has no email infrastructure, so the caller (an
// owner/admin) is responsible for copying it and delivering it out-of-band.
export function inviteMember(email: string, role: pb.AccountRole): Promise<pb.InviteMemberResponse> {
  const r = new pb.InviteMemberRequest()
  r.setEmail(email)
  r.setRole(role)
  return callConsoleCustomer('inviteMember', r)
}

export function removeMember(userId: string): Promise<pb.RemoveMemberResponse> {
  const r = new pb.RemoveMemberRequest()
  r.setUserId(userId)
  return callConsoleCustomer('removeMember', r)
}

export function setMemberRole(userId: string, newRole: pb.AccountRole): Promise<pb.SetMemberRoleResponse> {
  const r = new pb.SetMemberRoleRequest()
  r.setUserId(userId)
  r.setNewRole(newRole)
  return callConsoleCustomer('setMemberRole', r)
}

// acceptInvite is UNAUTHENTICATED — no session cookie exists yet at the
// point this is called (a fresh invitee following a link out-of-band). See
// client.ts's callConsoleCustomerUnauthenticated for why this cannot go
// through the ordinary callConsoleCustomer path.
export function acceptInvite(token: string, newPassword: string): Promise<pb.AcceptInviteResponse> {
  const r = new pb.AcceptInviteRequest()
  r.setToken(token)
  r.setNewPassword(newPassword)
  return callConsoleCustomerUnauthenticated('acceptInvite', r)
}
